import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/admin/DashboardLayout";

const FIELDS = [["title", "Titolo (H1)"], ["excerpt", "Estratto"], ["meta_title", "Meta title (≤60)"], ["meta_description", "Meta description (≤155)"], ["cover_image", "URL immagine di copertina"], ["cover_alt", "Alt text immagine"]];

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/admin/articles/${id}`).then((r) => setA(r.data)).catch(() => navigate("/dashboard/content")); }, [id, navigate]);
  if (!a) return <p className="text-muted-foreground">Caricamento...</p>;

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = { title: a.title, excerpt: a.excerpt, content: a.content, meta_title: a.meta_title, meta_description: a.meta_description, cover_image: a.cover_image, cover_alt: a.cover_alt, category: a.category, pet_type: a.pet_type, ...extra };
      const { data } = await api.patch(`/admin/articles/${id}`, body);
      setA(data); toast.success(extra.status === "published" ? "Pubblicato!" : "Salvato");
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="article-editor-page">
      <Link to="/dashboard/content" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={14} /> Content Engine</Link>
      <PageHeader eyebrow={`/blog/${a.slug} · ${a.status === "published" ? "Pubblicato" : "Bozza"}`} title="Revisione articolo">
        <button onClick={() => setPreview(!preview)} className="btn-ghost !py-2 text-sm" data-testid="editor-preview-toggle">{preview ? "Modifica" : "Anteprima"}</button>
        <button onClick={() => save()} disabled={saving} className="btn-ghost !py-2 text-sm" data-testid="editor-save-btn"><Save size={14} /> Salva</button>
        {a.status !== "published" && <button onClick={() => save({ status: "published" })} disabled={saving} className="btn-primary !py-2 text-sm" data-testid="editor-publish-btn"><CheckCircle2 size={14} /> Approva e pubblica</button>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="surface p-5">
          {preview ? <div className="prose-pet" data-testid="editor-preview"><ReactMarkdown remarkPlugins={[remarkGfm]}>{a.content}</ReactMarkdown></div>
            : <textarea className="input min-h-[70vh] font-mono text-xs leading-relaxed" value={a.content} onChange={(e) => setA({ ...a, content: e.target.value })} data-testid="editor-content-textarea" />}
        </div>
        <aside className="space-y-4">
          {FIELDS.map(([k, l]) => (
            <div key={k}><label className="text-xs font-medium text-muted-foreground">{l}</label>
              {k === "excerpt" ? <textarea rows={3} className="input mt-1 text-sm" value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} data-testid={`editor-field-${k}`} />
                : <input className="input mt-1 text-sm" value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} data-testid={`editor-field-${k}`} />}
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-muted-foreground">Categoria</label><select className="input mt-1 text-sm" value={a.category} onChange={(e) => setA({ ...a, category: e.target.value })} data-testid="editor-field-category">{["costi", "confronti", "guide", "recensioni"].map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-medium text-muted-foreground">Animale</label><select className="input mt-1 text-sm" value={a.pet_type} onChange={(e) => setA({ ...a, pet_type: e.target.value })} data-testid="editor-field-pet-type">{["entrambi", "cane", "gatto"].map((c) => <option key={c}>{c}</option>)}</select></div>
          </div>
          <div className="surface p-4 text-xs text-muted-foreground"><p className="font-medium text-foreground mb-1">Keywords</p>{a.keywords?.join(", ") || "—"}<p className="font-medium text-foreground mt-3 mb-1">FAQ ({a.faq?.length || 0})</p>{a.faq?.map((f) => <p key={f.q} className="mb-1">• {f.q}</p>)}</div>
        </aside>
      </div>
    </div>
  );
}
